import { logAmplitudeEvent } from 'amplitude/amplitude'
import { authSelectors } from 'ducks/auth'
import { getPremiumProducts, premiumProductsSelectors } from 'ducks/premiumProducts'
import { useErrorHandling, useNotify } from 'ra-core'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import restClient from 'restClient'
import { PremiumProductDetailType } from 'types/global'

interface PremiumProductsProps {
  productsLoading: boolean
  availableProducts: PremiumProductDetailType[] | null
}

export const usePremiumProducts = () => {
  const dispatch = useDispatch()
  const orgId = useSelector(authSelectors.getOrgId)
  const productsLoading = useSelector(premiumProductsSelectors.getIsLoading)
  const availableProducts = useSelector(premiumProductsSelectors.getProducts)
  const loadedOrgId = useSelector(premiumProductsSelectors.getLoadedOrgId)

  useEffect(() => {
    if (!orgId) {
      return
    }
    if (productsLoading) {
      return
    }
    if (loadedOrgId === orgId && availableProducts !== null) {
      return
    }
    dispatch(getPremiumProducts(orgId))
  }, [orgId, productsLoading, loadedOrgId, availableProducts, dispatch])

  return useMemo((): PremiumProductsProps => {
    return { productsLoading, availableProducts }
  }, [productsLoading, availableProducts])
}

export const useGetPremiumProductByTitle = () => {
  const { productsLoading, availableProducts } = usePremiumProducts()
  return useMemo(() => {
    return (targetProductTitle: string): PremiumProductDetailType | null => {
      if (productsLoading || !availableProducts) {
        console.info('Premium wallet products not ready', { productsLoading, availableProducts })
        return null
      }
      const prod = availableProducts.find((prod) => prod.title === String(targetProductTitle))
      if (!prod) {
        console.error(`Product with ID ${targetProductTitle} not found.`, {
          productsLoading,
          availableProducts,
        })
        return null
      }
      return prod
    }
  }, [productsLoading, availableProducts])
}

interface UseUpdatePremiumProductsProps {
  wallet_products: string[]
  onActivationSuccess?: () => void
}

export const useUpdatePremiumProducts = () => {
  const notify = useNotify()
  const restClientInstance = restClient(window.API_ROOT + '/api')
  const dispatch = useDispatch()
  const orgId = useSelector(authSelectors.getOrgId)
  const errorHandling = useErrorHandling()
  return ({ wallet_products, onActivationSuccess }: UseUpdatePremiumProductsProps) => {
    //TODO: refactor to use react-admin's DataProvider
    return restClientInstance('CUSTOM_PUT', 'custom', {
      url: `org_enable_products/${orgId}/`,
      data: { wallet_products },
    })
      .then((response: any) => {
        notify('Your change has been saved', 'success')
        // clear cache, changes to premium imagery subscriptions should force re-fetching of available imagery on next trip to design page
        window.AccountHelper.terrainUrlsCache.deleteAll()
        dispatch(getPremiumProducts(orgId))
        onActivationSuccess?.()
      })
      .catch((err) => {
        errorHandling.data_provider(err, { msg: 'We were unable to save your changes' })
        logAmplitudeEvent('premium_product_change_error', {})
      })
  }
}

export const useSendPvfActivationEmail = () => {
  const orgId = useSelector(authSelectors.getOrgId)
  const restClientInstance = restClient(window.API_ROOT + '/api')
  return () => {
    return restClientInstance('CUSTOM_POST', 'custom', {
      url: `orgs/${orgId}/activate_pvf_integration/`,
    })
      .then((response) => {
        if (response.data.status === true) {
          return response
        }
      })
      .catch((err) => {
        console.error('Error sending PVF enabled emails', err)
      })
  }
}

interface UseAcceptTermsAndActivateProductProps {
  targetProduct: PremiumProductDetailType
  onActivationSuccess?: () => void
}

export const useAcceptTermsAndActivateProduct = () => {
  const orgId = useSelector(authSelectors.getOrgId)
  const updatePremiumProducts = useUpdatePremiumProducts()
  const notify = useNotify()

  const { productsLoading, availableProducts } = usePremiumProducts()

  return ({ targetProduct, onActivationSuccess }: UseAcceptTermsAndActivateProductProps) => {
    if (productsLoading || !availableProducts) {
      notify('Something went wrong', 'error')
      return
    } else {
      const activeProductsUrls: string[] = availableProducts
        .filter((p) => targetProduct.url !== p.url && p.is_active)
        .map((p) => p.url)
      if (targetProduct.is_active) {
        console.warn('Product was already activated according to state.', { targetProduct, activeProductsUrls })
      }
      updatePremiumProducts({ wallet_products: [...activeProductsUrls, targetProduct.url], onActivationSuccess })
    }
  }
}
